import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Factory, Award, Wrench, Layers, Star } from "lucide-react";
import Counter from "@/components/Counter";
import SectionHeading from "@/components/SectionHeading";
import { useContent } from "@/context/ContentContext";
import { imgSrc } from "@/lib/api";
import SEO from "@/components/SEO";

const ICONS = [Layers, Wrench, Factory, Award];

export default function Home() {
  const { content, reviews } = useContent();
  const previewReviews = reviews.slice(0, 3);
  const H = content.hero, L = content.leadership, M = content.manufacturing, P = content.partnership, CS = content.cta_strip;

  return (
    <div data-testid="home-page">
      <SEO
        path="/"
        title="Premium Aluminium Dealer · Chennai"
        description="Victory Aluminium & Hardwares — Chennai's trusted aluminium dealer since 1998. Aluminium sections, City BOND ACP, HPL panels, doors, windows, partitions, hardware. Authorised City BOND distributor. 40+ years experience. Pan-India supply."
      />
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={imgSrc(H.image)} alt="Modern aluminium glass facade" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-black/80"></div>
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pb-24 pt-40 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: "easeOut" }}>
            <div className="flex items-center gap-3 mb-7">
              <span className="divider-gold"></span>
              <span className="tracking-eyebrow text-[#D4AF37]">{H.eyebrow}</span>
            </div>
            <h1 className="font-display text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-5xl">
              {H.headline_1} <br />{H.headline_2} <span className="italic text-[#D4AF37]">{H.headline_accent}</span> {H.headline_3}
            </h1>
            <p className="mt-7 text-[#D1D5DB] text-base md:text-lg max-w-2xl leading-relaxed">{H.subheadline}</p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/products" data-testid="hero-explore-products" className="btn-gold inline-flex items-center gap-2 px-7 py-4 text-xs tracking-widest uppercase font-semibold">
                {H.cta_primary} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" data-testid="hero-contact-cta" className="inline-flex items-center gap-2 px-7 py-4 text-xs tracking-widest uppercase font-semibold border border-white/40 text-white hover:bg-white hover:text-[#1A1A1A] transition-colors">
                {H.cta_secondary}
              </Link>
            </div>
          </motion.div>

          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {content.stats.map((s, i) => (
              <div key={i} data-testid={`stat-${i}`} className="bg-black/40 backdrop-blur-md px-5 py-7">
                <div className="text-[#D4AF37] text-3xl md:text-4xl"><Counter end={s.value} suffix={s.suffix} /></div>
                <div className="mt-2 text-[#D1D5DB] text-xs tracking-wider uppercase">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HERITAGE */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Heritage · Trust · Engineering" title="A legacy forged in aluminium." sub={content.about.sub} />
            <Link to="/about" data-testid="home-about-cta" className="mt-8 inline-flex items-center gap-2 text-sm tracking-widest uppercase font-semibold text-[#1A1A1A] border-b-2 border-[#D4AF37] pb-1">
              Discover Our Story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-5">
            {content.verticals.map((v, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <div key={v.title} className="bg-[#FAFAFA] border border-[#E5E7EB] p-6 hover:border-[#D4AF37] transition-colors">
                  <Icon className="w-7 h-7 text-[#D4AF37] mb-4" />
                  <h3 className="font-display text-lg leading-snug">{v.title}</h3>
                  <p className="mt-2 text-sm text-[#4B5563]">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LEADERSHIP TEASER */}
      <section className="py-24 bg-[#0F0F10] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="aspect-[4/5] overflow-hidden bg-[#1A1A1A]">
              <img src={imgSrc(L.image)} alt={L.name} className="w-full h-full object-cover hover:scale-105 transition-all duration-700" />
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <SectionHeading invert eyebrow={L.eyebrow} title={L.title} sub={L.body} />
            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {L.points.map((pt) => (
                <div key={pt.title} className="border-l-2 border-[#D4AF37] pl-4">
                  <div className="font-display text-lg">{pt.title}</div>
                  <div className="text-sm text-[#9CA3AF] mt-1">{pt.desc}</div>
                </div>
              ))}
            </div>
            <Link to="/about" data-testid="home-leadership-cta" className="mt-10 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-semibold text-[#D4AF37]">
              Read Leadership Story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FOUNDER LEGACY (optional — visible only when enabled) */}
      {content.founder?.enabled && (
        <section className="py-24 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-14 items-start">
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] overflow-hidden bg-[#F3F4F6]">
                {content.founder.image
                  ? <img src={imgSrc(content.founder.image)} alt={content.founder.name} className="w-full h-full object-cover hover:scale-105 transition-all duration-700" />
                  : <div className="w-full h-full flex items-center justify-center text-[#9CA3AF] text-sm">Add a photo from /admin</div>}
              </div>
              <div className="mt-6">
                <div className="font-display text-2xl text-[#1A1A1A]">{content.founder.name}</div>
                <div className="text-sm text-[#4B5563] mt-1">{content.founder.designation}</div>
                {content.founder.years_active && <div className="text-xs text-[#6B7280] mt-1 tracking-widest uppercase">{content.founder.years_active}</div>}
              </div>
            </div>
            <div className="lg:col-span-7">
              <SectionHeading eyebrow={content.founder.eyebrow} title={content.founder.title} sub={content.founder.body} />
              {Array.isArray(content.founder.points) && content.founder.points.length > 0 && (
                <div className="mt-10 grid sm:grid-cols-2 gap-5">
                  {content.founder.points.map((pt) => (
                    <div key={pt.title} className="border-l-2 border-[#D4AF37] pl-4">
                      <div className="font-display text-lg">{pt.title}</div>
                      <div className="text-sm text-[#4B5563] mt-1">{pt.desc}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* MANUFACTURING + CITY BOND */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 grid md:grid-cols-2 gap-6">
          <Link to="/manufacturing" data-testid="home-mfg-card" className="group relative overflow-hidden bg-[#1A1A1A] aspect-[4/5] md:aspect-auto md:min-h-[520px]">
            <img src={imgSrc(M.visuals[0]?.src)} alt="Manufacturing" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
            <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-12 text-white">
              <span className="tracking-eyebrow text-[#D4AF37]">{M.eyebrow}</span>
              <h3 className="font-display text-3xl md:text-4xl mt-3 leading-tight">{M.title}</h3>
              <p className="mt-4 text-sm text-[#D1D5DB] max-w-md">Partner in Star Aluminium & Alloys — {M.capacity_mt.toLocaleString("en-IN")} MT per annum.</p>
              <span className="mt-6 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-semibold text-[#D4AF37]">Explore <ArrowRight className="w-4 h-4" /></span>
            </div>
          </Link>

          <Link to="/partnership" data-testid="home-citybond-card" className="group relative overflow-hidden bg-[#1A1A1A] aspect-[4/5] md:aspect-auto md:min-h-[520px]">
            <img src={imgSrc(P.image)} alt="City Bond ACP" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
            <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-12 text-white">
              <span className="tracking-eyebrow text-[#D4AF37]">{P.eyebrow}</span>
              <h3 className="font-display text-3xl md:text-4xl mt-3 leading-tight">{P.title}</h3>
              <p className="mt-4 text-sm text-[#D1D5DB] max-w-md">{P.body}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-semibold text-[#D4AF37]">Explore <ArrowRight className="w-4 h-4" /></span>
            </div>
          </Link>
        </div>
      </section>

      {/* REVIEWS PREVIEW */}
      <section className="py-24 bg-[#FAFAFA]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
            <SectionHeading eyebrow="Trusted by Our Customers" title="Voices from architects, builders, and homeowners." sub="Independently shared reviews from clients across India." />
            <Link to="/reviews" data-testid="home-reviews-cta" className="text-xs tracking-widest uppercase font-semibold text-[#1A1A1A] border-b-2 border-[#D4AF37] pb-1 inline-flex items-center gap-2">
              All Reviews <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {previewReviews.map((r) => (
              <div key={r.id} className="bg-white border border-[#E5E7EB] p-7 hover:border-[#D4AF37] transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-[#1A1A1A] text-[#D4AF37] flex items-center justify-center font-display">{r.avatar_initials}</div>
                  <div>
                    <div className="font-semibold text-sm">{r.name}</div>
                    <div className="text-xs text-[#6B7280]">{r.profession}</div>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 mt-4 text-[#D4AF37]">
                  {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-[#D4AF37]" />)}
                </div>
                <p className="mt-4 text-sm text-[#4B5563] leading-relaxed line-clamp-5">{r.text}</p>
                <div className="mt-5 flex items-center justify-between text-[10px] uppercase tracking-widest text-[#9CA3AF]">
                  <span>{r.relative_time}</span>
                  <span className="font-semibold text-[#4285F4]">G · Google</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-[#1A1A1A] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <h3 className="font-display text-3xl md:text-4xl leading-tight">{CS.title} <br /><span className="text-[#D4AF37]">{CS.accent}</span></h3>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3 flex-wrap">
            <Link to="/contact" data-testid="cta-strip-contact" className="btn-gold px-7 py-4 text-xs tracking-widest uppercase font-semibold">{CS.primary}</Link>
            <Link to="/products" data-testid="cta-strip-products" className="px-7 py-4 text-xs tracking-widest uppercase font-semibold border border-white/40 hover:bg-white hover:text-[#1A1A1A] transition-colors">{CS.secondary}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
